// `Button` (design-system spec §3.10) — the one button primitive. 29 px
// (sm 26; ≥44 at phone widths via the stylesheet), radius 6, three variants:
// secondary (default), primary (ONE per view — review-enforced), danger
// (destructive only). `busy` disables and shows an inline spinner without a
// width change. Links are never styled as buttons (gate §4.3b): a navigation
// CTA is a Button whose onClick navigates.

import type { ButtonHTMLAttributes, ReactNode } from "react";
import { useReadOnlyReason } from "./read-only";

export type ButtonVariant = "secondary" | "primary" | "danger";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: "md" | "sm";
  /** Disables the button and overlays an inline spinner, width unchanged. */
  busy?: boolean;
  /**
   * The label while `busy` ("Saving…"). It is NOT decoration: a control whose
   * ACCESSIBLE NAME is unchanged while its mutation is in flight is a control
   * that reports "done" the instant it is clicked — it is disabled either way.
   * A certified walkthrough (M01-S04) waits for `Save components` to go
   * disabled as its "the save landed" signal, and dropping the pending label at
   * UX-2 turned that wait into a race it lost on a cold replica. Any button
   * whose completion is observable through the control itself passes this.
   */
  busyLabel?: ReactNode;
  children?: ReactNode;
}

export function Button({
  variant = "secondary",
  size = "md",
  busy = false,
  busyLabel,
  disabled,
  children,
  className,
  type,
  title,
  ...rest
}: ButtonProps) {
  // A submit inside a read-only boundary (a tenant suspended in grace) cannot
  // submit, and says why (components/read-only.tsx).
  const readOnlyReason = useReadOnlyReason();
  const refused = type === "submit" && readOnlyReason !== null;
  const classes = ["ds-btn"];
  if (variant === "primary") classes.push("ds-btn-primary");
  if (variant === "danger") classes.push("ds-btn-danger");
  if (size === "sm") classes.push("ds-btn-sm");
  if (busy) classes.push("ds-btn-busy");
  if (className !== undefined) classes.push(className);
  return (
    <button
      type={type ?? "button"}
      className={classes.join(" ")}
      disabled={disabled === true || busy || refused}
      aria-busy={busy || undefined}
      title={refused ? readOnlyReason : title}
      {...rest}
    >
      {busy && busyLabel !== undefined ? busyLabel : children}
      {busy ? (
        <span className="ds-btn-spin" aria-hidden="true">
          …
        </span>
      ) : null}
    </button>
  );
}
